import React, { useState, useEffect } from 'react';
import { getCertificates } from '../../services/api';

function CertificateManager() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getCertificates();
      if (response.data.success) {
        setCertificates(response.data.certificates);
      } else {
        const errorMsg = typeof response.data.error === 'object' 
          ? response.data.error.message || 'Failed to load certificates'
          : response.data.error || 'Failed to load certificates';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = typeof err.response?.data?.error === 'object'
        ? err.response.data.error.message || 'Failed to load certificates'
        : err.response?.data?.error || err.message || 'Failed to load certificates';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isExpiringSoon = (expirationDate) => {
    const daysUntilExpiration = Math.floor(
      (new Date(expirationDate) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiration < 30 && daysUntilExpiration >= 0;
  };

  const isExpired = (expirationDate) => {
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span className="alert-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div>
      <p className="text-secondary" style={{ marginBottom: '20px' }}>
        Certificates are used to sign your apps and establish trust with Apple's infrastructure.
      </p>

      {certificates.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📜</div>
          <p className="empty-state-title">No certificates found</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Expiration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map(cert => {
                const expiring = isExpiringSoon(cert.attributes?.expirationDate || cert.expirationDate);
                const expired = isExpired(cert.attributes?.expirationDate || cert.expirationDate);
                
                return (
                  <tr key={cert.id}>
                    <td>{cert.attributes?.name || cert.name || 'N/A'}</td>
                    <td className="mono" style={{ fontSize: '13px' }}>
                      {cert.attributes?.certificateType || cert.certificateType || 'N/A'}
                    </td>
                    <td>
                      <span className={expired ? 'text-error' : expiring ? 'text-warning' : ''}>
                        {new Date(cert.attributes?.expirationDate || cert.expirationDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${expired ? 'badge-error' : expiring ? 'badge-warning' : 'badge-success'}`}>
                        {expired ? 'Expired' : expiring ? 'Expiring Soon' : 'Valid'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert alert-info" style={{ marginTop: '20px' }}>
        <span className="alert-icon">💡</span>
        <span>
          <strong>Tip:</strong> Certificates are typically managed through Xcode or the Apple Developer portal.
          Use the CLI tool for advanced certificate operations.
        </span>
      </div>
    </div>
  );
}

export default CertificateManager;
